import { useCallback, useState } from 'react';

/*
 * Хук для отслеживания статуса асинхронной операции
 * промис всегда должен возвращать статус `fulfilled`, а статус `rejected` должен быть отловлен внутри промиса
 *
 * @example
 *
 *  ```tsx
 *   const { isPending, wrapPromise } = useIsPending();
 *
 *   const handleSubmit = useCallback(async () => {
 *      await wrapPromise(submitForm());
 *   },[])
 *
 *   <Button isLoading={isPending} onClick={handleSubmit}>Submit</Button>
 *   ```
 *
 * @param initialValue - начальное значение
 */
export function useIsPending(initialValue: boolean = false) {
    const [error, setError] = useState<unknown>(null);

    if (__IS_DEV__ && error) {
        throw error;
    }

    const [isPending, setIsPending] = useState(initialValue);

    const setPending = useCallback(() => setIsPending(true), []);
    const setFulfilled = useCallback(() => setIsPending(false), []);

    const wrapPromise = useCallback(async <T = unknown>(promise: Promise<T>) => {
        let result: T | undefined;

        setIsPending(true);

        try {
            result = await promise;
        } catch (error) {
            console.error(error);

            if (__IS_DEV__) {
                console.warn(
                    'Promise переданный в useIsPending не должен выбрасывать исключения. Необходимо отловить исключения promise до передачи в wrapPromise'
                );
                setError(new Error('Promise переданный в useIsPending выбросил исключение.'));
            }
        } finally {
            setIsPending(false);
        }

        return result;
    }, []);

    return { isPending, setIsPending, setPending, setFulfilled, wrapPromise };
}
